import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  QueryParamsBuilder,
  QueryParamsBuilderService,
} from './query-params-builder.service';
import { ApiResponse } from '../models/api-reponse';
import { HomePageDTO } from '../models/home-page-dto';
import { ReagentsPageDTO } from '../models/reagents-page-dto';
import { SystemsPageDTO } from '../models/systems-page-dto';
import { CompanyPageDTO } from '../models/company-page-dto';
import { JobsPageDTO } from '../models/jobs-page-dto';
import { SupportPageDTO } from '../models/support-page-dto';
import { ProductDTO } from '../models/product-dto';
import { SystemDTO } from '../models/system-dto';
import { ContactPageDTO } from '../models/contact-page-dto';
import { TermsAndConditionsPageDTO } from '../models/terms-and-conditions-page-dto';
import { PrivacyPolicyPageDTO } from '../models/privacy-policy-page-dto';
import { LegalNoticePageDTO } from '../models/legal-notice-page-dto';
import { NotFoundPageDTO } from '../models/not-found-page-dto';
import { MailDTO } from '../models/mail-dto';
import { ProductCategoryDTO } from '../models/product-category-dto';
import { ReagentsArchivePageDTO } from '../models/reagents-archive-page-dto';
import { ProductGroupDTO } from '../models/product-group-dto';
import { ArchivedProductDTO } from '../models/archived-product-dto';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private readonly client = inject(HttpClient);
  private readonly queryParamsBuilder = inject(QueryParamsBuilderService);

  private readonly strapiApiUrl = `${environment.strapiBaseUrl}/api`;
  private readonly defaultStrapiHeader: Record<string, string> = {
    Authorization: `Bearer ${environment.strapiAccessToken}`,
  };
  private getDefaultPopulatedEndpoint$<T>(
    endpoint: string,
  ): Observable<ApiResponse<T>> {
    return this.client.get<ApiResponse<T>>(`${this.strapiApiUrl}/${endpoint}`, {
      headers: this.defaultStrapiHeader,
      params: this.getDefaultQueryBuilderForPage().build(),
    });
  }

  private getDefaultQueryBuilderForPage(): QueryParamsBuilder {
    return this.queryParamsBuilder
      .new()
      .setModulePopulationParam('standard-media-carousel', '[items][populate]')
      .setModulePopulationParam('hero-media-carousel', '[items][populate]')
      .setModulePopulationParam(
        'product-media-carousel',
        '[products][populate]',
      )
      .setModulePopulationParam('job-media-carousel', '[job_entries][populate]')
      .setModulePopulationParam('system-media-carousel', '[systems][populate]')
      .setModulePopulationParam('copy-text', '[cta][populate]')
      .setModulePopulationParam('copy-text', '[files][populate]')
      .setModulePopulationParam('revealing-text', '[populate]')
      .setModulePopulationParam('jump-marker', '[populate]')
      .setModulePopulationParam('explainer-text', '[keywords][populate]')
      .setModulePopulationParam('innovation-section', '[items][populate]')
      .setModulePopulationParam('simple-gallery', '[items][populate]')
      .setModulePopulationParam('navigation-gallery', '[items][populate]')
      .setModulePopulationParam('toggle-list', '[items][populate]')
      .setModulePopulationParam('placeholder', '[media][populate]')
      .setModulePopulationParam('featured-faq', '[items][populate]')
      .setModulePopulationParam('faq', '[items][populate]')
      .setModulePopulationParam('tech-facts', '[items][populate]')
      .setModulePopulationParam('history-slider', '[items][populate]')
      .setModulePopulationParam('cta', '[cta][populate]')
      .setModulePopulationParam('single-image', '[media][populate]')
      .setModulePopulationParam('contact-form', '[populate]');
  }

  public getHomePage$(): Observable<ApiResponse<HomePageDTO>> {
    // TODO: refactor to home-page in strapi / api.service to 'home-page'
    return this.getDefaultPopulatedEndpoint$<HomePageDTO>('homepage');
  }

  public getReagentsPage$(): Observable<ApiResponse<ReagentsPageDTO>> {
    // TODO: refactor to reagents-page in strapi / api.service to 'reagent-page'
    return this.getDefaultPopulatedEndpoint$<ReagentsPageDTO>('reagent');
  }

  public getReagentsArchivePage$(): Observable<
    ApiResponse<ReagentsArchivePageDTO>
  > {
    // TODO: refactor to reagents-archive-page in strapi / api.service to 'reagent-archive-page'
    return this.client.get<ApiResponse<ReagentsArchivePageDTO>>(
      `${this.strapiApiUrl}/reagents-archive-page`,
      {
        headers: this.defaultStrapiHeader,
        params: this.getDefaultQueryBuilderForPage()
          .setModulePopulationParam('product-archive', '')
          .build(),
      },
    );
  }

  public getSystemsPage$(): Observable<ApiResponse<SystemsPageDTO>> {
    // TODO: refactor to systems-page in strapi / api.service to 'system-page'
    return this.getDefaultPopulatedEndpoint$<SystemsPageDTO>('system');
  }

  public getSystemDetails$(slug: string): Observable<ApiResponse<SystemDTO>> {
    return this.client
      .get<ApiResponse<SystemDTO[]>>(`${this.strapiApiUrl}/device-systems`, {
        headers: this.defaultStrapiHeader,
        params: this.getDefaultQueryBuilderForPage()
          .setParam('filters[slug][$eq]', slug)
          .build(),
      })
      .pipe(map(res => ({ data: res.data[0] })));
  }

  public getReagentsDetails$(
    slug: string,
  ): Observable<ApiResponse<ProductDTO>> {
    return this.client
      .get<ApiResponse<ProductDTO[]>>(`${this.strapiApiUrl}/products`, {
        headers: this.defaultStrapiHeader,
        params: this.getDefaultQueryBuilderForPage()
          .setParam('filters[slug][$eq]', slug)
          .build(),
      })
      .pipe(map(res => ({ data: res.data[0] })));
  }

  public getCompanyPage$(): Observable<ApiResponse<CompanyPageDTO>> {
    // TODO: refactor to company-page in strapi / api.service to 'company-page'
    return this.getDefaultPopulatedEndpoint$<CompanyPageDTO>('company');
  }

  public getJobsPage$(): Observable<ApiResponse<JobsPageDTO>> {
    // TODO: refactor to jobs-page in strapi / api.service to 'jobs-page'
    return this.getDefaultPopulatedEndpoint$<JobsPageDTO>('job');
  }

  public getNotFoundPage$(): Observable<ApiResponse<NotFoundPageDTO>> {
    return this.getDefaultPopulatedEndpoint$<NotFoundPageDTO>('not-found-page');
  }

  public getSupportPage$(): Observable<ApiResponse<SupportPageDTO>> {
    return this.getDefaultPopulatedEndpoint$<SupportPageDTO>('support-page');
  }

  public getContactPage$(): Observable<ApiResponse<ContactPageDTO>> {
    return this.getDefaultPopulatedEndpoint$<ContactPageDTO>('contact-page');
  }

  public getLegalNoticePage$(): Observable<ApiResponse<LegalNoticePageDTO>> {
    return this.getDefaultPopulatedEndpoint$<LegalNoticePageDTO>(
      'legal-notice-page',
    );
  }

  public getPrivacyPolicyPage$(): Observable<
    ApiResponse<PrivacyPolicyPageDTO>
  > {
    return this.getDefaultPopulatedEndpoint$<PrivacyPolicyPageDTO>(
      'privacy-policy-page',
    );
  }

  public getTermsAndConditionsPage$(): Observable<
    ApiResponse<TermsAndConditionsPageDTO>
  > {
    return this.getDefaultPopulatedEndpoint$<TermsAndConditionsPageDTO>(
      'terms-and-conditions-page',
    );
  }

  public getSystemItems$(): Observable<ApiResponse<SystemDTO[]>> {
    return this.client.get<ApiResponse<SystemDTO[]>>(
      `${this.strapiApiUrl}/device-systems?populate=media`,
      {
        headers: this.defaultStrapiHeader,
      },
    );
  }

  public getProductItems$(): Observable<ApiResponse<ProductDTO[]>> {
    return this.client.get<ApiResponse<ProductDTO[]>>(
      `${this.strapiApiUrl}/products?populate=media&populate=manual`,
      {
        headers: this.defaultStrapiHeader,
      },
    );
  }

  public getBySlug(type: string, slug: string): Observable<boolean> {
    let endpoint: string;
    switch (type) {
      case 'systems':
        endpoint = 'device-systems';
        break;
      case 'reagents':
        endpoint = 'products';
        break;
      default:
        return of(false);
    }

    /* eslint-disable  @typescript-eslint/no-explicit-any */
    return this.client
      .get<ApiResponse<any[]>>(`${this.strapiApiUrl}/${endpoint}`, {
        headers: this.defaultStrapiHeader,
        params: this.queryParamsBuilder
          .new()
          .setParam('filters[slug][$eq]', slug)
          .build(),
      })
      .pipe(map(response => response.data && response.data.length > 0));
  }

  public getProductCategorieItems$(): Observable<
    ApiResponse<ProductCategoryDTO[]>
  > {
    return this.client.get<ApiResponse<ProductCategoryDTO[]>>(
      `${this.strapiApiUrl}/product-categories`,
      {
        headers: this.defaultStrapiHeader,
        params: this.queryParamsBuilder.new().build(),
      },
    );
  }

  public getProductGroupItems$(): Observable<ApiResponse<ProductGroupDTO[]>> {
    return this.client.get<ApiResponse<ProductGroupDTO[]>>(
      `${this.strapiApiUrl}/product-groups`,
      {
        headers: this.defaultStrapiHeader,
        params: this.queryParamsBuilder.new().build(),
      },
    );
  }

  public getProductArchiveItems$(): Observable<
    ApiResponse<ArchivedProductDTO[]>
  > {
    return this.client.get<ApiResponse<ArchivedProductDTO[]>>(
      `${this.strapiApiUrl}/archived-products`,
      {
        headers: this.defaultStrapiHeader,
        params: this.queryParamsBuilder
          .new()
          .setModulePopulationParam('copy-text', '[files][populate]')
          .setParam('populate[groups][populate]', '*')
          .setParam('populate[categories][populate]', '*')
          .build(),
      },
    );
  }

  public sendMail(data: MailDTO): Observable<ApiResponse<string>> {
    return this.client
      .post<ApiResponse<string>>(`${this.strapiApiUrl}/send-email`, data, {});
  }
}
